import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumCustomerHappinessRating } from "./enums/enumCustomerHappinessRating.generated";

// Model Objects

export class LeadCommunicationLogEntry extends ModelObject<LeadCommunicationLogEntry> {

    public id: Guid = Guid.createEmpty();
    /**
    * Id of the communication log's communication subject.
    */
    public communicationSubjectId: Guid = Guid.createEmpty();
    /**
    * Id of the communication log's communication type.
    */
    public communicationTypeId: Guid = Guid.createEmpty();
    /**
    * Rating of the customer happiness.
    */
    public customerHappinessRating: EnumCustomerHappinessRating = EnumCustomerHappinessRating.neutral;
    /**
    * Description of the communication log entry.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public description: string = "";
    /**
    * Lead relating to which the communication occurred.
    */
    public leadId: Guid = Guid.createEmpty();
    /**
    * Timestamp at which the communication occurred.
    */
    public timestamp: Instant = Instant.ofEpochMilli(0);
    /**
    * User who created the communication log entry.
    */
    public userId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.communicationSubjectId = Guid.parseOrEmpty(jsonSerializedObject.communicationSubjectId);
          this.communicationTypeId = Guid.parseOrEmpty(jsonSerializedObject.communicationTypeId);
          this.customerHappinessRating = EnumCustomerHappinessRating.getByValue(jsonSerializedObject.customerHappinessRating, this.customerHappinessRating);
          this.description = jsonSerializedObject.description;
          this.leadId = Guid.parseOrEmpty(jsonSerializedObject.leadId);
          this.timestamp = Instant.parse(jsonSerializedObject.timestamp);
          this.userId = Guid.parseOrEmpty(jsonSerializedObject.userId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.communicationSubjectId = this.communicationSubjectId.toString();
        toRet.communicationTypeId = this.communicationTypeId.toString();
        toRet.customerHappinessRating = this.customerHappinessRating.value;
        toRet.description = this.description;
        toRet.leadId = this.leadId.toString();
        toRet.timestamp = this.timestamp.toString();
        toRet.userId = this.userId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "LeadCommunicationLogEntry.Id cannot be empty"},
            {field: "communicationSubjectId", rule: (v: any) => !v.isEmpty() || "LeadCommunicationLogEntry.CommunicationSubjectId cannot be empty"},
            {field: "communicationTypeId", rule: (v: any) => !v.isEmpty() || "LeadCommunicationLogEntry.CommunicationTypeId cannot be empty"},
            {field: "leadId", rule: (v: any) => !v.isEmpty() || "LeadCommunicationLogEntry.LeadId cannot be empty"},
            {field: "userId", rule: (v: any) => !v.isEmpty() || "LeadCommunicationLogEntry.UserId cannot be empty"},
    ];
}


export class LeadLogEntryFilter extends ModelObject<LeadLogEntryFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _leadIds: readonly (Guid)[] | null = null;
    public get leadIds(): readonly (Guid)[] | null { return this._leadIds }
    public set leadIds(value: readonly (Guid)[] | null) {
      this._leadIds = value === undefined ? null : value;
    }
    private _userIds: readonly (Guid)[] | null = null;
    public get userIds(): readonly (Guid)[] | null { return this._userIds }
    public set userIds(value: readonly (Guid)[] | null) {
      this._userIds = value === undefined ? null : value;
    }
    private _fromTimestamp: Instant | null = null;
    /**
    * Minimum (inclusive) timestamp.
    */
    public get fromTimestamp(): Instant | null { return this._fromTimestamp }
    public set fromTimestamp(value: Instant | null) {
      this._fromTimestamp = value === undefined ? null : value;
    }
    private _toTimestamp: Instant | null = null;
    /**
    * Maximum (inclusive) timestamp.
    */
    public get toTimestamp(): Instant | null { return this._toTimestamp }
    public set toTimestamp(value: Instant | null) {
      this._toTimestamp = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.leadIds = jsonSerializedObject.leadIds == null ? null : jsonSerializedObject.leadIds.map((v: any) => Guid.parseOrEmpty(v));
          this.userIds = jsonSerializedObject.userIds == null ? null : jsonSerializedObject.userIds.map((v: any) => Guid.parseOrEmpty(v));
          this.fromTimestamp = jsonSerializedObject.fromTimestamp == null ? null : Instant.parse(jsonSerializedObject.fromTimestamp);
          this.toTimestamp = jsonSerializedObject.toTimestamp == null ? null : Instant.parse(jsonSerializedObject.toTimestamp);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.leadIds = this.leadIds == null ? null : this.leadIds.map(v => v.toString());
        toRet.userIds = this.userIds == null ? null : this.userIds.map(v => v.toString());
        toRet.fromTimestamp = this.fromTimestamp == null ? null : this.fromTimestamp.toString();
        toRet.toTimestamp = this.toTimestamp == null ? null : this.toTimestamp.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class FilteredLeadLogEntryResults extends ModelObject<FilteredLeadLogEntryResults> {

    public matchingCount: number = 0;
    public leadLogEntries: (LeadCommunicationLogEntry)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.leadLogEntries = jsonSerializedObject.leadLogEntries.map((v: any) => new LeadCommunicationLogEntry(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.leadLogEntries = this.leadLogEntries.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "leadLogEntries", rule: (v: any) => !!v || "leadLogEntries is required"},
            {field: "leadLogEntries", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as LeadCommunicationLogEntry[]).reduce((acc: Array<string>, curr: LeadCommunicationLogEntry) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class LeadCommunicationLogService extends ServiceBase {

  public async addLeadLogEntry(communicationSubjectId: Guid, communicationTypeId: Guid, customerHappinessRating: EnumCustomerHappinessRating, description: string, leadId: Guid, userId: Guid, timestampIfNotDbNow: Instant | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<LeadCommunicationLogEntry> {
    const req = {
    tenantId:api.tenantId.toString(),
    communicationSubjectId:communicationSubjectId.toString(),
    communicationTypeId:communicationTypeId.toString(),
    customerHappinessRating:customerHappinessRating.value,
    description:description,
    leadId:leadId.toString(),
    userId:userId.toString(),
    timestampIfNotDbNow:timestampIfNotDbNow == null ? null : timestampIfNotDbNow.toString(),
    };
    const extractor = (response:any) => new LeadCommunicationLogEntry(response.leadLogEntry);;
    return this.makeJsonRequest("leadCommunicationLog/v1/addLeadLogEntry", req, false, extractor, opts);
  };

  public async getLeadLogEntry(leadLogEntryId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<LeadCommunicationLogEntry> {
    const req = {
    tenantId:api.tenantId.toString(),
    leadLogEntryId:leadLogEntryId.toString(),
    };
    const extractor = (response:any) => new LeadCommunicationLogEntry(response.leadLogEntry);;
    return this.makeJsonRequest("leadCommunicationLog/v1/getLeadLogEntry", req, false, extractor, opts);
  };

  public async getLeadLogEntries(leadLogEntryIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(LeadCommunicationLogEntry)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    leadLogEntryIds:leadLogEntryIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.leadLogEntries.map((v: any) => new LeadCommunicationLogEntry(v));;
    return this.makeJsonRequest("leadCommunicationLog/v1/getLeadLogEntries", req, false, extractor, opts);
  };

  public async getLeadLogEntriesByFilter(filter: LeadLogEntryFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredLeadLogEntryResults> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredLeadLogEntryResults(response.results);;
    return this.makeJsonRequest("leadCommunicationLog/v1/getLeadLogEntriesByFilter", req, false, extractor, opts);
  };

  public async getLastLeadLogEntries(leadIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(LeadCommunicationLogEntry)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    leadIds:leadIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.leadLogEntry.map((v: any) => new LeadCommunicationLogEntry(v));;
    return this.makeJsonRequest("leadCommunicationLog/v1/getLastLeadLogEntries", req, false, extractor, opts);
  };
}

export const leadCommunicationLogService = new LeadCommunicationLogService();